import React, { Component } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import log from '../utils/logger';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        log.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen bg-black flex items-center justify-center p-4">
                    <div className="bg-gray-900 rounded-lg p-8 max-w-md w-full text-center">
                        <div className="text-red-500 mb-4 flex justify-center">
                            <AlertTriangle size={48} />
                        </div>

                        <h2 className="text-white text-xl font-bold mb-4">
                            Oops! Qualcosa è andato storto
                        </h2>

                        <p className="text-gray-400 mb-6">
                            Si è verificato un errore inaspettato. Prova a ricaricare la pagina.
                        </p>

                        {import.meta.env.MODE === 'development' && this.state.error && (
                            <div className="bg-gray-800 rounded p-3 mb-4 text-left">
                <pre className="text-red-400 text-xs overflow-auto">
                  {this.state.error.message}
                </pre>
                            </div>
                        )}

                        <div className="space-y-3">
                            <button
                                onClick={this.handleRetry}
                                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-colors flex items-center justify-center space-x-2"
                            >
                                <RefreshCw size={16} />
                                <span>Riprova</span>
                            </button>

                            <button
                                onClick={() => window.location.reload()}
                                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded transition-colors"
                            >
                                Ricarica Pagina
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;